import { createStore, useStore } from 'zustand'

/**
 * Cross-tree state for the by-post layout: whether the posts sidepanel is
 * collapsed.
 *
 * The "New comments available" pill is centred over the comments column, so it
 * has to know how wide the posts sidepanel currently is. That width follows the
 * panel's collapsed state, which is local to the by-post view, while the pill
 * renders in the page header above it. A CSS custom property cannot travel that
 * way and a prop would have to thread up through the header, so this module
 * carries the flag between them.
 *
 * False outside the by-post view: the flat list view has no posts sidepanel.
 */
type PostsPanelState = {
  isCollapsed: boolean
  setCollapsed: (isCollapsed: boolean) => void
}

const postsPanelStore = createStore<PostsPanelState>()((set, get) => ({
  isCollapsed: false,

  setCollapsed: (isCollapsed): void => {
    if (get().isCollapsed === isCollapsed) return
    set({ isCollapsed })
  },
}))

/** Publish whether the posts sidepanel is collapsed. */
export const setPostsPanelCollapsed = (isCollapsed: boolean): void =>
  postsPanelStore.getState().setCollapsed(isCollapsed)

/** Read the collapsed state without subscribing. */
export const getPostsPanelCollapsed = (): boolean =>
  postsPanelStore.getState().isCollapsed

/** Whether the posts sidepanel is collapsed. */
export const useIsPostsPanelCollapsed = (): boolean =>
  useStore(postsPanelStore, (state) => state.isCollapsed)
